import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { combineQueries } from '@datorama/akita';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { Bites } from '@modules/teeth-diagram/models/bites.enum';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { FeatureToggleSettings, SoftwareOptions } from '@shared/models/enums/enums';
import { LimitedFeatures } from '@shared/models/limited-features';
import { ProcedureMap } from '@shared/models/procedure-map';
import { AddRxService } from '@shared/services/add-rx.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { VersionsService } from '@shared/services/versions.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ScanOptionsQuery } from './state/scan-options-query';
import { initializeAdditionalBites, ScanOptionsStore } from './state/scan-options-store';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';

@Injectable()
export class ScanOptionsFacade {
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('scanOptions');
	isRxTakenForScan$ = this.shellQuery.isRxTakenForScan$;

	private isMultiLayerImplantBase$: Observable<boolean> = this.hostPlatformService.isScanner$.pipe(
		switchMap(isScanner => {
			return isScanner ? this.isMultiLayerImplantBaseEnabledForScanner : this.isMultiLayerImplantBaseEnabledForCompany;
		})
	);

	private hasImplanedBasedPrep$: Observable<boolean> = this.teethDiagramQuery.teeth$.pipe(
		map(teeth => {
			return [...teeth.lowerJaw, ...teeth.upperJaw].some(
				tooth =>
					tooth.UnitTypeID === UnitTypes.ImplantBased ||
					(tooth.ToothInBridgeTypeID as UnitTypesInBridge) === UnitTypesInBridge.ImplantBased
			);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private isMultiLayerImplantBaseEnabledForScanner: Observable<boolean> = combineLatest([
		this.shellQuery.clientVersion$,
		this.shellQuery.packageVersion$,
		this.orderQuery.procedureMap$,
		this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.MultiLayerImplantBased),
		this.hasImplanedBasedPrep$
	]).pipe(
		map(([clientVersion, packageVersion, procedureMap, hasSwo, hasImplanedBasedPrep]) => {
			const scannerCompatible = this.versionService.isFeatureAvailableForScanner(
				LimitedFeatures.MultiLayerImplantBased,
				clientVersion,
				packageVersion
			);
			const isFixedRestorativeProcedure = (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;

			return scannerCompatible && isFixedRestorativeProcedure && hasSwo && hasImplanedBasedPrep;
		})
	);

	private isMultiLayerImplantBaseEnabledForCompany: Observable<boolean> = combineLatest([
		this.shellQuery.scannersVersions$,
		this.orderQuery.procedureMap$,
		this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.MultiLayerImplantBased),
		this.hasImplanedBasedPrep$
	]).pipe(
		map(([scannerVersions, procedureMap, hasSwo, hasImplanedBasedPrep]) => {
			const versions = scannerVersions?.map(s => s.VersionNumber);
			const scannersCompatible = this.versionService.isFeatureAvailableForAllScanners(
				LimitedFeatures.MultiLayerImplantBased,
				versions
			);
			const isFixedRestorativeProcedure = (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;

			return scannersCompatible && isFixedRestorativeProcedure && hasSwo && hasImplanedBasedPrep;
		})
	);

	isNiriCaptureVisible$: Observable<boolean> = this.shellQuery.isNiriCaptureVisible$.pipe(map(x => !!x));
	isNiriCaptureChecked$: Observable<boolean> = this.query.isNiriCaptureChecked$;
	isRxForModelingContext$: Observable<boolean> = this.hostPlatformService.isIteroModeling$;
	isNiriCaptureDisabled$: Observable<boolean> = combineLatest([
		this.isReadOnly$,
		this.shellQuery.isRxTakenForScan$,
		this.addRxService.isNiriReadOnly$
	]).pipe(
		map(([isReadOnly, isRxTakenForScan, isNiriReadOnlyAddRx]) => {
			return isReadOnly || isRxTakenForScan || isNiriReadOnlyAddRx;
		})
	);

	isSleeveConfirmationVisible$: Observable<boolean> = this.shellQuery.isSleeveConfirmationVisible$.pipe(map(x => !!x));
	isSleeveChecked$: Observable<boolean> = this.query.isSleeveChecked$.pipe(map(x => !!x.value));
	isSleeveConfirmationReadOnly$: Observable<boolean> = this.shellQuery.isSleeveConfirmationReadOnly$.pipe(map(x => !!x));

	isEmergenceProfileChecked$: Observable<boolean> = this.query.isEmergenceProfileChecked$;
	isEmergenceProfileVisible$: Observable<boolean> = combineLatest([
		this.isMultiLayerImplantBase$,
		this.isReadOnly$,
		this.isEmergenceProfileChecked$
	]).pipe(
		map(([isMultiLayerImplantBase, isReadOnly, isEmergenceProfileChecked]) => {
			return this.hostPlatformService.isIteroModeling || (isReadOnly && isEmergenceProfileChecked) || isMultiLayerImplantBase;
		})
	);
	isEmergenceProfileDisabled$: Observable<boolean> = combineLatest([this.shellQuery.isRxSent$, this.shellQuery.isRxScanned$]).pipe(
		map(([isRxSent, isRxScanned]) => this.hostPlatformService.isIteroModeling || isRxSent || isRxScanned)
	);

	isPreTreatmentVisible$: Observable<boolean> = combineQueries([
		this.shellQuery.isPreTreatmentVisibleForV0$,
		this.orderQuery.procedureMap$
	]).pipe(map(([isPreTreatmentVisibleForV0, procedureMap]) => isPreTreatmentVisibleForV0 || !!procedureMap?.PreTreatmentScanIsEnabled));
	isPreTreatmentChecked$: Observable<boolean> = this.query.isPreTreatmentChecked$;
	isPreTreatmentReadOnly$: Observable<boolean> = combineLatest([
		this.shellQuery.isRxSent$,
		this.isReadOnly$,
		this.addRxService.isPreTreatmentReadOnly$,
		this.shellQuery.isRxScanned$
	]).pipe(
		map(([isRxSent, isReadOnly, isPreTreatmentReadOnlyAddRx, isScanned]) => {
			return isRxSent || isReadOnly || isScanned || isPreTreatmentReadOnlyAddRx;
		})
	);

	private isRestoMultiBiteFF$: Observable<boolean> = this.shellQuery.featureToggles$.pipe(
		map(featureToggles => featureToggles?.find(f => f.id === String(FeatureToggleSettings.RestorativeMultiBite))?.isActive ?? false)
	);

	isRestoMultiBiteSelected$: Observable<boolean> = this.query.isRestoMultiBiteSelected$;
	isRestoMultiBiteVisible$: Observable<boolean> = combineLatest([
		this.hostPlatformService.isScanner$,
		this.shellQuery.isRxSent$,
		this.isRestoMultiBiteFF$,
		this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.RestorativeMultiBite),
		this.orderQuery.procedureMap$,
		this.isRestoMultiBiteSelected$
	]).pipe(
		switchMap(
			([isScanner, isRxSent, hasFf, hasSwo, procedureMap, isRestoMultiBiteSelected]: [
				boolean,
				boolean,
				boolean,
				boolean,
				ProcedureMap,
				boolean
			]) => {
				if (isRxSent) {
					return of(isRestoMultiBiteSelected);
				}

				if (
					(procedureMap?.ProcedureId as ProcedureEnum) !== ProcedureEnum.FixedRestorative ||
					(!hasFf && !hasSwo) ||
					this.orderQuery.isGlidewellOrder
				) {
					return of(false);
				}

				return isScanner ? this.isRestoMultiBiteAvailableForScanner$ : this.isRestoMultiBiteAvailableForAllScanners$;
			}
		)
	);

	isMultiBiteVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(map(x => !!x?.MultiBiteIsEnabled));
	isMultiBiteSelected$: Observable<boolean> = this.orderQuery.isMultiBiteSelected$;
	additionalBites$: Observable<Record<string, boolean>> = this.query.additionalBites$;
	procedureMap$: Observable<Maybe<ProcedureMap>> = this.orderQuery.procedureMap$;

	isDentureCopyScan$: Observable<boolean> = this.dentureDetailsQuery.isDentureCopyScan$;
	isDentureCopyScanVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => !!procedureMap?.DentureCopyScanIsEnabled)
	);

	shouldShowMultiBiteInformationMessage$: Observable<boolean> = combineQueries([
		this.orderQuery.procedureMap$,
		this.isMultiBiteSelected$
	]).pipe(
		map(
			([procedureMap, isMultiBiteSelected]) =>
				(procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.StudyModel_iRecord && isMultiBiteSelected
		)
	);

	isPalatalFeedbackChecked$: Observable<boolean> = this.query.isPalatalFeedbackChecked$;

	isPalatalFeedbackVisible$: Observable<boolean> = this.shellQuery.isPalatalFeedbackVisible$.pipe(map(x => !!x));

	isDisabled$: Observable<boolean> = combineLatest([this.isReadOnly$, this.isRxTakenForScan$]).pipe(
		map(([isReadOnly, isRxTakenForScan]) => !!(isReadOnly || isRxTakenForScan))
	);
	isAdditionalBitesVisible$: Observable<boolean> = combineLatest([this.isRestoMultiBiteVisible$, this.isRestoMultiBiteSelected$]).pipe(
		map(([isVisible, isSelected]) => isVisible && isSelected)
	);

	private isRestoMultiBiteAvailableForScanner$: Observable<boolean> = combineLatest([
		this.shellQuery.clientVersion$,
		this.shellQuery.packageVersion$
	]).pipe(
		map(([clientVersion, packageVersion]) => {
			return this.versionService.isFeatureAvailableForScanner(LimitedFeatures.RestorativeMultiBite, clientVersion, packageVersion);
		})
	);
	private isRestoMultiBiteAvailableForAllScanners$: Observable<boolean> = this.shellQuery.scannersVersions$.pipe(
		map(scannersVersions => {
			const versions = scannersVersions?.map(s => s.VersionNumber);

			return this.versionService.isFeatureAvailableForAllScanners(LimitedFeatures.RestorativeMultiBite, versions);
		})
	);

	protected palatalFeedbackDefaultValue$: Observable<boolean> = combineLatest([
		this.shellQuery.palatalFeedbackDefaultValue$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(([palatalFeedbackDefaultValue, procedureMap]) =>
			(procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.StudyModel_iRecord ? false : !!palatalFeedbackDefaultValue
		)
	);

	constructor(
		protected readonly store: ScanOptionsStore,
		protected readonly query: ScanOptionsQuery,
		protected readonly shellQuery: ShellQuery,
		protected readonly logger: LoggerService,
		protected readonly orderStore: OrderStore,
		protected readonly orderQuery: OrderQuery,
		protected readonly dentureDetailsStore: DentureDetailsStore,
		protected readonly dentureDetailsQuery: DentureDetailsQuery,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly softwareOptionsService: SoftwareOptionsService,
		protected readonly hostPlatformService: HostPlatformService,
		protected readonly versionService: VersionsService,
		protected readonly addRxService: AddRxService
	) {}

	updateSleeveConfirmation(value: boolean) {
		this.logger.info(`updateSleeveConfirmation: with-> ${value} `, { module: 'ScanOptionsFacade' });
		this.store.update({ isSleeveChecked: { value, isValueChangedByUser: true } });
	}

	updatePreTreatmentConfirmation({ isPreTreatmentChecked }: { isPreTreatmentChecked: boolean }) {
		this.logger.info(`updatePreTreatment: with-> ${isPreTreatmentChecked} `, { module: 'ScanOptionsFacade' });
		this.store.update({ isPreTreatmentChecked, isPreTreatmentUserSelected: true });
	}

	updateNiriCapture({ isNiriCaptureChecked }: { isNiriCaptureChecked: boolean }) {
		this.logger.info(`updateNiriCapture: with-> ${isNiriCaptureChecked} `, { module: 'ScanOptionsFacade' });
		this.store.update({ isNiriCaptureChecked, isNiriUserSelected: true });
	}

	updatePalatalFeedback({ isPalatalFeedbackChecked }: { isPalatalFeedbackChecked: boolean }) {
		this.logger.info(`updatePalatalFeedback: with-> ${isPalatalFeedbackChecked} `, { module: 'ScanOptionsFacade' });
		this.store.update({
			isPalatalFeedbackChecked,
			userSelectedPalatalFeedback: isPalatalFeedbackChecked // persist user choice
		});
	}

	loadNiri(): Observable<unknown> {
		return combineLatest([this.shellQuery.niriCaptureDefaultValue$, this.shellQuery.rx$, this.shellQuery.isNiriCaptureVisible$]).pipe(
			tap(([niriCaptureDefaultValue, rx, isNiriCaptureVisible]) => {
				if (!rx) {
					const state = this.store.getValue();

					if (!state.isNiriUserSelected) {
						this.store.update({ isNiriCaptureChecked: (niriCaptureDefaultValue && isNiriCaptureVisible) ?? false });
					}

					return;
				}

				const isNiriChecked = typeof rx.NIRIEnabled === 'boolean' ? rx.NIRIEnabled : niriCaptureDefaultValue;

				this.store.update({ isNiriCaptureChecked: (isNiriChecked && isNiriCaptureVisible) ?? false });
			})
		);
	}

	loadAdditionalBites() {
		return this.shellQuery.rx$.pipe(
			tap(rx => {
				if (rx) {
					const additionalBites = initializeAdditionalBites();

					if (rx.MultiBiteRestoScan?.Includes?.length) {
						rx.MultiBiteRestoScan.Includes.forEach(key => (additionalBites[key as Bites] = true));
					}

					this.updateAdditionalBites(additionalBites);
				}
			})
		);
	}

	loadMultiBiteRestoScan() {
		return this.shellQuery.rx$.pipe(
			tap(rx => {
				if (rx) {
					const isRestoMultiBiteSelected = rx.MultiBiteRestoScan?.Enabled ?? false;

					this.store.update({ isRestoMultiBiteSelected });
				}
			})
		);
	}

	handlePalatalFeedback(): Observable<unknown> {
		return combineLatest([
			this.shellQuery.palatalScanningFeedback$,
			this.shellQuery.isPalatalFeedbackVisible$,
			this.palatalFeedbackDefaultValue$
		]).pipe(
			tap(([rxPalatalScanningFeedback, isVisible, defaultPalatalScanningFeedback]) => {
				this.updatePalatalScanningFeedbackCheckState(!!isVisible, rxPalatalScanningFeedback, defaultPalatalScanningFeedback);
			})
		);
	}

	updateScanOptionsMultiBite(isMultiBite: boolean): void {
		this.orderStore.update({ isMultiBiteSelected: isMultiBite });
	}

	updateScanOptionsRestoMultiBite(isRestoMultiBiteSelected: boolean): void {
		this.store.update({ isRestoMultiBiteSelected });

		if (!isRestoMultiBiteSelected) {
			const bites = initializeAdditionalBites();

			this.updateAdditionalBites(bites);
		}
	}

	loadSleeveConfirmation(): Observable<boolean> {
		return combineLatest([this.shellQuery.isRxSent$, this.shellQuery.rx$, this.query.isSleeveCheckedSentByScanner$]).pipe(
			map(([isRxSent, rx, isSleeveCheckedSentByScanner]) => {
				// this value is used by scanner in cases when doctor returns from scan page to rx page and already confirmed new sleeve.
				if (!!isSleeveCheckedSentByScanner) {
					return true;
				}
				if (!isRxSent) {
					return false;
				}

				return !!rx?.IsSleeveConfirmed;
			}),
			tap((value: boolean) => this.store.update({ isSleeveChecked: { value, isValueChangedByUser: false } }))
		);
	}

	loadPreTreatmentAndEmergenceProfile(): Observable<unknown> {
		return combineLatest([this.shellQuery.isRxSent$, this.shellQuery.rx$]).pipe(
			tap(([, rx]: [boolean, Maybe<RxModel>]) => {
				if (!rx) {
					const state = this.store.getValue();

					if (!state.isPreTreatmentUserSelected) {
						this.store.update({ isPreTreatmentChecked: false });
					}

					if (!state.isEmergenceProfileUserSelected) {
						this.store.update({ isEmergenceProfileChecked: false });
					}

					return;
				}

				const isPreTreatmentChecked = typeof rx.PrePrepScan === 'boolean' ? rx.PrePrepScan : false;
				const isEmergenceProfileChecked = rx.EmergenceProfile ?? false;

				this.store.update({
					isPreTreatmentChecked: isPreTreatmentChecked ?? false,
					isEmergenceProfileChecked
				});
			})
		);
	}

	updateScanOptionsDentureCopyScan(isDentureCopyScan: boolean): void {
		this.dentureDetailsStore.update({ isDentureCopyScan });
	}

	updateEmergencyProfile(isEmergenceProfileChecked: boolean): void {
		this.store.update({ isEmergenceProfileChecked, isEmergenceProfileUserSelected: true });
	}

	updateAdditionalBites(bites: Partial<Record<Bites, boolean>>) {
		this.store.update({ additionalBites: { ...this.query.additionalBites, ...bites } });
	}

	getSrcForRadioButtonImg(checked: Maybe<boolean>): string {
		return `${this.shellQuery.staticFilesEndpoint}/assets/icons/${checked ? '' : 'un'}check.svg`;
	}

	isBiteSelected(biteId: string): Observable<boolean> {
		return this.additionalBites$.pipe(map(bite => bite[biteId] || false));
	}

	protected updatePalatalScanningFeedbackCheckState(
		isVisible: boolean,
		rxPalatalScanningFeedback: boolean | undefined,
		defaultPalatalScanningFeedback: boolean
	) {
		if (!isVisible) {
			this.store.update({ isPalatalFeedbackChecked: false });

			return;
		}

		const state = this.store.getValue();

		this.store.update({
			isPalatalFeedbackChecked: state.userSelectedPalatalFeedback ?? rxPalatalScanningFeedback ?? defaultPalatalScanningFeedback
		});
	}
}
